import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Stack, Typography } from "@mui/material";
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ClickAndCollectLabelSX, ClickAndCollectPickUpSx, ClickAndCollectSx, StoreListSX } from "./styles/container";
import { useLocalization } from "@/data/Localization";
import { useProductDetails } from "@/data/Content/ProductDetails";
import { ContentContext, ContentProvider } from '@/data/context/content';
import { ID } from "@/data/types/Basic";
import { StoreStockAutoComplete } from "../StoreStockAutoComplete";
import { displayStoreWithStock, LatLng } from "@/data/types/Store";
import { useSettings } from "@/data/Settings";
import { CheckCircleOutlined as DelTypeCheckedIcon } from '@mui/icons-material';
import { StoreDetailsDisplay } from "./parts/StoreDetailsDisplay";
import { CCStoreSearchAutoCompleteOptions, CLICK_AND_COLLECT, HOME_DELIVERY } from "@/data/constants/clickAndCollect";
import { getCookie } from "@/utils/clickAndCollect";
import { useStoreLocatorState } from "@/data/state/useStoreLocatorState";
import { StoreListActions } from "./parts/StoreListActions";
import { useClickAndCollect } from "@/data/Content/ClickAndCollect";
import { SelectedStoreDisplay } from "./parts/SelectedStore";
import { EMPTY_STRING } from "@/data/constants/marketing";
import { hideIfEmpty } from "@/components/layouts/styles/main";
import { combineSX } from "@/utils/combineSX";

export const PDPClickAndCollect:FC<{ id: ID}> = ({id}) => {
   
    const {settings} = useSettings();    
    
    const autoCompleteOptions = useMemo(()=>CCStoreSearchAutoCompleteOptions(settings), [settings]);

    const productDetail = useLocalization("productDetail");
    const clickAndCollectNLS = useLocalization("ClickAndCollect");
    const storeStockNLS = useLocalization('StoreStock');

    const [latLng, setLatLng] = useState<LatLng>({} as LatLng);
    const [showSelectedStore, setShowSelectedStore] = useState(false);

    const {
		deliveryType, setDeliveryType, productAttrValues, hasInventory, isInventoryLoading, product,
        isDeliverySectionSticky, calculatedPrice, storeLocator, actions
	} = useContext(ContentContext) as ReturnType<typeof useProductDetails>  &
            ReturnType<typeof useStoreLocatorState> & 
            {isDeliverySectionSticky:boolean};

    const allowedPickStore = productAttrValues.ITNL_PickupInStore==="true";
    const isPickupOnlyItem = productAttrValues.ITNL_PickupInStoreOnly === "true";

    const clickAndCollectValues = useClickAndCollect(id?[id.toString()]:[]);

    const {
        setDisplayStores,
        setStoreUniqueIds,
        selectedStoreCC,
        setSelectedStoreCC,
        showNostoresWithStockMsg,
        setStores,
        isSapStockStatusAvailable
    } = clickAndCollectValues;

    const { canBuy, disableHomeDelivery, disableClickAndCollect } = useMemo(() => {
        const canBuy =
            hasInventory &&
            product?.buyable === "true" &&
            calculatedPrice !== EMPTY_STRING;

        const disableHomeDelivery = !canBuy || isPickupOnlyItem;

        const disableClickAndCollect = !isPickupOnlyItem && (!allowedPickStore || !canBuy);

        return {
            canBuy,
            disableHomeDelivery,
            disableClickAndCollect,
        };
    }, [
        product?.buyable,
        hasInventory,
        calculatedPrice,
        isPickupOnlyItem,
        allowedPickStore,
    ]);

    useEffect(() => {
        const selectedStoreId = getCookie("WC_pickUpStore");

        if (typeof selectedStoreId === "string" && selectedStoreId) {
            setStoreUniqueIds([selectedStoreId]);
            setDeliveryType(CLICK_AND_COLLECT);
        }
    }, []);

    useEffect(() => {
        setShowSelectedStore(selectedStoreCC ? true : false)
    }, [selectedStoreCC]);

    useEffect(() => {
        if (!canBuy) return;

        // force switch to CC when home delivery not allowed
        const shouldForceCC =
            deliveryType === HOME_DELIVERY &&
            ((!isInventoryLoading && !hasInventory) || isPickupOnlyItem);

        if (shouldForceCC) {
            setDeliveryType(CLICK_AND_COLLECT);
            return;
        }

        // validate selected CC store
        if (!selectedStoreCC) return;

        const isStoreDontHaveStock = selectedStoreCC.stock === "OutOfStock" || selectedStoreCC.stock === "CallStoreStock";

        if (isStoreDontHaveStock) {
            setSelectedStoreCC(undefined);
            if (deliveryType !== HOME_DELIVERY && !isPickupOnlyItem) {
                setDeliveryType(HOME_DELIVERY);
            }
            return;
        }

        onSetSelectedStore(selectedStoreCC);
    }, [
        selectedStoreCC,
        hasInventory,
        isInventoryLoading,
        canBuy,
        isPickupOnlyItem,
        deliveryType,
    ]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const tempDeliveryType = event.target.value;

        if (tempDeliveryType === HOME_DELIVERY) {
            setStores([]);
            setDisplayStores([]);
            setSelectedStoreCC(undefined);
        }

        setDeliveryType(tempDeliveryType);
    };

    const onSetSelectedStore = useCallback((store: displayStoreWithStock | undefined) => {
        if (store) {
            setSelectedStoreCC(store);
            actions?.selectStore(store.store);
        } else {
            setSelectedStoreCC(undefined);
            actions?.resetSelectedStore();
        }

        // common cleanup
        setDisplayStores([]);
        setStores([]);
    }, [actions]);
 //   const prodSAPStatus:string = getAttributeValueByIdentifier(product?.attributes,"ITNL_Winkel SAP Status");

    return(
        <ContentProvider value={{storeLocator, actions, ...clickAndCollectValues}}>
            <Box className="pdp-click-and-collect" sx={ClickAndCollectSx({isSticky:isDeliverySectionSticky})}>
                <Typography 
                    variant="body1" 
                    fontFamily="mikado-regular" 
                    fontWeight="bold" 
                    fontSize={18}
                >{clickAndCollectNLS.DeliveryTypes.t()}</Typography>
                <FormControl sx={{width:'100%', '& .MuiRadio-root':{py:isDeliverySectionSticky?0.5:1}}}>
                    <RadioGroup
                        aria-labelledby="controlled-radio-buttons-group"
                        name="controlled-radio-buttons-group"
                        value={deliveryType}
                        onChange={handleChange}
                    >
                        <FormControlLabel 
                            value={HOME_DELIVERY} 
                            control={<Radio id={`checkbox_deliveryType_${HOME_DELIVERY}`} checkedIcon={<DelTypeCheckedIcon />} checked={deliveryType===HOME_DELIVERY} />}
                            id={`radio-group-delivery-type-${HOME_DELIVERY}`}
                            data-testid={`radio-group-delivery-type-${HOME_DELIVERY}`}
                            disabled = {disableHomeDelivery}
                            label={
                                <Typography fontWeight='bold' color="#313131">{productDetail.ShopOnline.t()}</Typography>
                            }
                        />
                        <FormControlLabel 
                            value={CLICK_AND_COLLECT} 
                            id={`radio-group-delivery-type-${CLICK_AND_COLLECT}`}
                            data-testid={`radio-group-delivery-type-${CLICK_AND_COLLECT}`}
                            disabled = {disableClickAndCollect}
                            control={
                                <Radio 
                                    checkedIcon={<DelTypeCheckedIcon />}
                                    checked={deliveryType===CLICK_AND_COLLECT}
                                    id={`checkbox_deliveryType_${CLICK_AND_COLLECT}`}
                                />
                            } 
                            label={
                                    <>
                                        <Typography className="click-and-collect-label">{productDetail.ClickAndCollect.t()}</Typography>
                                        {allowedPickStore? <Typography className={allowedPickStore?"cc-usp-text":"cc-usp-text disabled"}>{clickAndCollectNLS.ClickAndCollectUSP.t()}</Typography> :null }
                                    </>
                            }
                            sx={ClickAndCollectLabelSX}
                        />
                    </RadioGroup>
                </FormControl>

                {deliveryType === CLICK_AND_COLLECT && allowedPickStore &&
                    <Stack sx={ClickAndCollectPickUpSx} rowGap={2}>
                        {!showSelectedStore && 
                            <>
                                <StoreStockAutoComplete {...{ options:autoCompleteOptions, latLngP:latLng}} />
                                <StoreListActions setLatLng={setLatLng} />
                            </>
                        }
                        <Stack sx={combineSX([hideIfEmpty, StoreListSX({isSticky:isDeliverySectionSticky})])}>
                            {!showSelectedStore && 
                                <>
                                    <StoreDetailsDisplay {...{setSelectedStore:onSetSelectedStore }} />
                                    {showNostoresWithStockMsg ?
                                        <Stack fontWeight='bold'>{clickAndCollectNLS.ClickAndCollectNoStoresWithStock.t()}</Stack>
                                    :null}
                                </>
                            }
                            {showSelectedStore && selectedStoreCC &&
                                <SelectedStoreDisplay setSelectedStore={onSetSelectedStore}/>
                            }
                        </Stack>
                        {!isSapStockStatusAvailable ? <Typography color="#d32f2f" p={1} pt={0}>{storeStockNLS.Error.SAPStockNotAvailable.t()}</Typography> : null}
                    </Stack>
                }
            </Box>
        </ContentProvider>
    );
}